import { createFileRoute, lazyRouteComponent, notFound } from "@tanstack/react-router"
import type { ComponentProps, FunctionComponent } from "react"

import { mdxComponents } from "@/components/content/mdx-overrides"
import { MdxProse } from "@/components/content/prose"
import type { ContentEntry } from "@/lib/content-resolver"
import { seoMeta } from "@/lib/seo"
import { SiteSection } from "@/routes/-components/site/SiteSection"

import { pressReleases } from "./-data/releases"

const modules = import.meta.glob("./-content/*.{md,mdx}") as Record<
  string,
  () => Promise<{ default: FunctionComponent<ComponentProps<ContentEntry["Content"]>> }>
>

const releaseComponents = Object.fromEntries(
  Object.entries(modules).map(([path, load]) => [path, lazyRouteComponent(load)]),
)

function getPressRelease(slug: string) {
  return pressReleases.find((r) => r.slug === slug)
}

export const Route = createFileRoute("/_content/news/$slug")({
  loader: async ({ params }) => {
    const release = getPressRelease(params.slug)
    if (!release) throw notFound()
    await releaseComponents[`./-content/${release.filename}`]!.preload?.()

    return release
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {}
    const { title, description } = loaderData
    const image = loaderData.image || `https://entire.com/og/news/${loaderData.slug}.png`
    const url = `https://entire.com/news/${loaderData.slug}`
    return {
      ...seoMeta({
        title: loaderData.ogTitle || title,
        description,
        url,
        image,
        type: "article",
        extra: [{ property: "article:published_time", content: loaderData.date }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "NewsArticle",
            headline: title,
            description,
            image,
            datePublished: loaderData.date,
            publisher: {
              "@type": "Organization",
              name: "Entire",
              logo: { "@type": "ImageObject", url: "https://entire.com/favicon.svg" },
            },
            mainEntityOfPage: { "@type": "WebPage", "@id": url },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://entire.com" },
              { "@type": "ListItem", position: 2, name: "News", item: "https://entire.com/news" },
              { "@type": "ListItem", position: 3, name: title, item: url },
            ],
          }),
        },
      ],
    }
  },
  component: PressReleaseRoute,
})

function PressReleaseRoute() {
  const data = Route.useLoaderData()
  const Content = releaseComponents[`./-content/${data.filename}`]!

  return (
    <SiteSection innerClassName="px-6 py-10 md:px-20">
      <article className="mx-auto flex w-full max-w-180 flex-col gap-8">
        <header className="flex flex-col gap-3">
          <h1 className="text-site-title-lg font-headline text-default">{data.title}</h1>
          <p className="text-site-label font-mono text-muted uppercase">{data.dateFormatted}</p>
        </header>
        {/* Releases write their own quotation marks, so drop the ones prose adds. */}
        <div className="text-site-body font-sans text-muted [&_blockquote_p]:before:content-none [&_blockquote_p]:after:content-none [&_blockquote_strong]:text-default">
          <MdxProse>
            <Content components={mdxComponents} />
          </MdxProse>
        </div>
      </article>
    </SiteSection>
  )
}
